import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Search, ShoppingCart, Store, MapPin, Plus, Sparkles, Utensils, Truck, ShoppingBag } from "lucide-react";
import { grocery, addToCart, cartTotals, formatXAF } from "../../lib/grocery";
import { toast } from "sonner";

/**
 * /restaurants/order — Click & Collect or Delivery for prepared meals.
 * Reuses the grocery cart + checkout infrastructure: restaurants are shops
 * with category_focus="restaurant_meal" and menu items are products.
 */
export default function RestaurantOrderPage() {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [meals, setMeals] = useState([]);
  const [zones, setZones] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [cart, setCart] = useState(cartTotals());

  useEffect(() => {
    grocery.shops({ category: "restaurant_meal" }).then(setShops).catch(() => setShops([]));
    grocery.zones().then((d) => setZones(d.zones || []));
    // List meals across all restaurants
    grocery.products({ category: "meal", limit: 60 }).then(setMeals).catch(() => setMeals([]));
  }, []);

  useEffect(() => {
    const refresh = () => setCart(cartTotals());
    window.addEventListener("kanama-cart-changed", refresh);
    return () => window.removeEventListener("kanama-cart-changed", refresh);
  }, []);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    grocery.products({ q: query.trim(), category: "meal", limit: 40 }).then(setResults).catch(() => setResults([]));
  }, [query]);

  const handleAdd = (m) => {
    addToCart(m, 1);
    toast.success(`${m.name} ajouté au panier`);
  };

  return (
    <div className="min-h-[80vh] bg-brand-paper" data-testid="restaurant-order-page">
      <section className="relative bg-brand-ink text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25 bg-gradient-to-br from-brand-gold/40 via-transparent to-brand-terracotta/30" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-gold/20 text-brand-gold text-xs uppercase tracking-[0.22em] font-bold mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Restaurants · N'Djamena
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight mb-3 inline-flex items-center gap-3">
            <Utensils className="w-9 h-9 text-brand-gold" /> Commander un repas
          </h1>
          <p className="text-base sm:text-lg text-white/85 max-w-2xl mb-6 leading-relaxed">
            Plats préparés, sandwiches, pizzas, cuisine tchadienne… choisissez la <strong className="text-brand-gold">livraison</strong> à domicile ou le <strong className="text-brand-gold">retrait en restaurant</strong>.
          </p>
          <div className="flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm">
              <Truck className="w-4 h-4 text-brand-gold" /> Livraison N'Djamena
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm">
              <ShoppingBag className="w-4 h-4 text-brand-gold" /> Retrait sur place · 0 FCFA
            </span>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-2xl p-2 max-w-xl shadow-xl mt-6">
            <Search className="w-5 h-5 text-brand-inkSoft ml-2 shrink-0" />
            <Input
              data-testid="restaurant-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Pizza, daraba, burger, sandwich…"
              className="border-0 shadow-none focus-visible:ring-0 text-brand-ink h-11"
            />
            <Button
              data-testid="restaurant-go-cart"
              onClick={() => navigate("/courses/panier")}
              className="rounded-xl bg-brand-gold hover:bg-brand-goldDark text-brand-black font-semibold relative h-11"
            >
              <ShoppingCart className="w-4 h-4 mr-1.5" />
              Panier
              {cart.count > 0 && (
                <span className="absolute -top-2 -right-2 bg-brand-terracotta text-white text-[10px] font-bold rounded-full px-1.5 min-w-[20px] h-5 flex items-center justify-center">
                  {cart.count}
                </span>
              )}
            </Button>
          </div>
        </div>
      </section>

      {/* Search results */}
      {query.trim() && (
        <section className="max-w-6xl mx-auto px-4 sm:px-8 pb-2 pt-6" data-testid="restaurant-results-section">
          <h2 className="font-heading text-xl font-semibold text-brand-ink mb-4">
            {results.length} résultat{results.length > 1 ? "s" : ""} pour "{query}"
          </h2>
          {results.length === 0 ? (
            <p className="text-brand-inkSoft">Aucun plat trouvé.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {results.map((m) => <MealCard key={m.id} meal={m} onAdd={handleAdd} />)}
            </div>
          )}
        </section>
      )}

      {/* Featured meals */}
      {!query.trim() && meals.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-8 pt-8 pb-2" data-testid="restaurant-meals-section">
          <h2 className="font-heading text-xl sm:text-2xl font-semibold text-brand-ink mb-4 inline-flex items-center gap-2">
            🍽️ Plats à la carte
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {meals.slice(0, 12).map((m) => <MealCard key={m.id} meal={m} onAdd={handleAdd} />)}
          </div>
        </section>
      )}

      {/* Restaurants list */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-8" data-testid="restaurant-shops-section">
        <h2 className="font-heading text-xl sm:text-2xl font-semibold text-brand-ink mb-4">Nos restaurants partenaires</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {shops.map((s) => (
            <Link key={s.id} to={`/courses/shop/${s.id}`} data-testid={`restaurant-shop-card-${s.id}`} className="block">
              <Card className="p-5 rounded-2xl border-brand-line hover:border-brand-gold transition-all hover:shadow-lg h-full">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-brand-sandlight flex items-center justify-center text-brand-gold shrink-0">
                    <Utensils className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-semibold text-brand-ink truncate">{s.name}</h3>
                    <div className="flex items-center gap-1 text-xs text-brand-inkSoft">
                      <MapPin className="w-3 h-3" />{zones.find((z) => z.slug === s.zone_slug)?.name || s.zone_slug}
                    </div>
                  </div>
                  {s.open && <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white">Ouvert</Badge>}
                </div>
                <p className="text-sm text-brand-inkSoft leading-relaxed line-clamp-2">{s.description}</p>
                <div className="text-[11px] text-brand-inkSoft mt-3 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-brand-gold" /> {s.address}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* Floating cart */}
      {cart.count > 0 && (
        <div
          className="md:hidden fixed left-3 right-3 z-40"
          style={{ bottom: "calc(72px + env(safe-area-inset-bottom, 0))" }}
          data-testid="restaurant-floating-cart"
        >
          <button
            onClick={() => navigate("/courses/panier")}
            className="w-full bg-brand-ink text-white rounded-2xl px-4 py-3 flex items-center justify-between shadow-2xl active:scale-[0.98] transition-transform"
          >
            <span className="flex items-center gap-2">
              <span className="bg-brand-gold text-brand-black rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold">{cart.count}</span>
              <span className="font-semibold">Voir mon panier</span>
            </span>
            <span className="text-brand-gold font-bold">{formatXAF(cart.subtotal_xaf)}</span>
          </button>
        </div>
      )}
    </div>
  );
}

function MealCard({ meal, onAdd }) {
  return (
    <Card className="p-3 rounded-2xl border-brand-line hover:border-brand-gold transition-all hover:shadow-md flex flex-col" data-testid={`restaurant-meal-${meal.id}`}>
      <div className="aspect-square rounded-xl bg-brand-sandlight flex items-center justify-center mb-2 overflow-hidden">
        <span className="text-5xl opacity-70">🍽️</span>
      </div>
      <h3 className="font-semibold text-brand-ink text-sm leading-tight line-clamp-2 mb-1">{meal.name}</h3>
      {meal.subtype && <div className="text-[11px] text-brand-inkSoft uppercase tracking-wider mb-2">{meal.subtype}</div>}
      <div className="flex items-center justify-between mt-auto gap-2">
        <span className="font-heading font-bold text-brand-goldDark text-base whitespace-nowrap">{formatXAF(meal.price_xaf)}</span>
        <Button size="sm" onClick={() => onAdd(meal)} className="h-8 px-2 rounded-lg bg-brand-ink hover:bg-brand-inkSoft text-white" data-testid={`restaurant-add-${meal.id}`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
}
